import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { getLocalizedString } from "@icarius-localization/strings";
import { CloseIcon } from "@icarius-icons";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  Select,
  MenuItem,
  InputLabel,
  Grid,
  Typography,
} from "@material-ui/core";
import { MuiPickersUtilsProvider, DatePicker } from "@material-ui/pickers";
import DialogTitleDivider from "@icarius-common/dialogTitleDivider";
import ButtonDialogAction from "@icarius-common/buttonDialogAction";
import PaperDraggable from "@icarius-common/paperDraggable";
import { openSnackbarAction } from "@icarius-common/snackbar/actions";
import DialogTransition from "@icarius-common/dialogTransition";
import DateFnsUtils from "@date-io/date-fns";
import { dateLocale } from "@icarius-localization/strings";
import { getPermissionList, getTypeList } from "@icarius-pages/digitalWarehouse/selectors";
import { createDigitalWarehouseItemAction, modifyDigitalWarehouseItemAction } from "@icarius-pages/digitalWarehouse/actions";
import * as yup from 'yup';
import { createDateFromDDMMYYYY, formatDateYYYYMMDD, getTomorrow } from "@icarius-utils/date";
import FileInput from "@icarius-common/fileUpload/fileInput";
import FileList from "@icarius-common/fileUpload/fileList";
import Loader from "@icarius-common/loader";

const gridStyle = { height: 75, padding: "0px 10px" };

const UploadFilesDialog = (props) => {

  const {
    open,
    isLoading,
    isEdit,
    data,
    groupCode,
    handleClose,
  } = props;

  const createDateFromMonth = () => {
    let monthToUse = data['month'];
    if (monthToUse.length === 1) {
      monthToUse = `0${monthToUse}`;
    }
    return createDateFromDDMMYYYY(`01/${monthToUse}/2020`)
  }


  const [failedDocuments, setFailedDocuments] = useState(null);
  const [files, setFiles] = useState(null);
  const [hasSetFile, setHasSetFile] = useState(false);

  const [type, setType] = useState((isEdit && data) ? data["type"] : "");
  const [description, setDescription] = useState((isEdit && data) ? data["description"] : "");
  const [permission, setPermission] = useState((isEdit && data) ? data["permissions"] : "L");

  const [folder, setFolder] = useState(data ? data["folder"] : "");
  const [sfolder, setSfolder] = useState(data ? data["sfolder"] : "");
  const [year, setYear] = useState(data?.year ? createDateFromDDMMYYYY(`01/12/${data["year"]}`) : null);
  const [month, setMonth] = useState(data?.month ? createDateFromMonth() : null);
  const [expirationDate, setExpirationDate] = useState(data?.expirationDate ? createDateFromDDMMYYYY(data.expirationDate) : null);

  useEffect(() => {
    Boolean(files) && setHasSetFile(true);
  }, [files])

  const dispatch = useDispatch();
  const typeList = useSelector(getTypeList);
  const permissionList = useSelector(getPermissionList);

  const hasFileIsValid = () => {
    if (Boolean(isEdit)) {
      if (hasSetFile) {
        return Boolean(files);
      }
      return true; // si no seteo archivo y es edit (Boolean(data)), esta bien que no haya archivo
    }

    return Boolean(files); // si es create, tiene que tener archivo
  }

  const handleSubmit = async () => {
    // ver si manda failed documents
    setFailedDocuments(null);
    if (await dataIsValid() && hasFileIsValid()) {
      const fileToSend = (() => { // si isEdit se manda como mucho un archivo solo
        if (isEdit) return files?.length ? files[0] : null;
        return files?.length ? files : null;
      })();

      let dataToSend = {
        file: fileToSend,
        type,
        description,
        folder,
        sfolder,
        permission,
        year: year ? year.getFullYear() : '',
        month: month ? month.getMonth() + 1 : '',
        groupCode,
        expirationDate: expirationDate ? formatDateYYYYMMDD(expirationDate) : null,
      }

      if (isEdit) {
        dataToSend.internalCode = data.internalCode;
      }

      dispatch(isEdit ? modifyDigitalWarehouseItemAction(dataToSend) : createDigitalWarehouseItemAction(dataToSend))
        .then((resp) => {
          if (resp?.status === 200) {
            handleClose();
          } else {
            if (resp?.data?.invalidFiles) {
              setFailedDocuments(resp.data.invalidFiles)
            }
          }
        });
    } else {
      dispatch(openSnackbarAction({ msg: getLocalizedString("enterRequiredFields"), severity: "error" }));
    }
  };

  const dataIsValid = async () => {
    const formData = {
      files,
      type,
      description,
      folder,
      sfolder,
      permission,
      year,
      month
    };

    const schema = yup.object().shape({
      type: yup.string().required(),
      description: yup.string().required(),
      permission: yup.string().required(),
      folder: yup.string().required(),
      sfolder: yup.string().required(),
    });

    return await schema.isValid(formData).then((valid) => valid);
  }

  const handleDeleteFileFromList = (file) => {
    const newFileArray = [...files];
    const indexToDelete = newFileArray.indexOf(file);
    if (indexToDelete > -1) {
      newFileArray.splice(indexToDelete, 1);
      setFiles(newFileArray.length === 0 ? null : newFileArray);
    }
  }

  return (
    <Dialog
      open={open}
      TransitionComponent={DialogTransition}
      PaperComponent={PaperDraggable}
      maxWidth={"md"}
      fullWidth={true}
    >
      <div className={"dialog-container"}>
        <DialogTitle style={{ cursor: 'move' }} id="draggable-dialog-title">
          {isEdit ? getLocalizedString("editFile") : getLocalizedString("createFile")}
          <DialogTitleDivider />
        </DialogTitle>
        <CloseIcon className={"dialog-close-icon icon"} onClick={handleClose} />
        <DialogContent>
          <Loader open={isLoading} />
          <Grid container item xs={12}>
            <Grid container item alignItems="center" xs={12} sm={6} style={gridStyle}>
              <TextField
                required
                value={folder}
                onChange={e => setFolder(e.target.value)}
                fullWidth
                label={getLocalizedString("folder")}
              />
            </Grid>
            <Grid container item alignItems="center" xs={12} sm={6} style={gridStyle}>
              <TextField
                required
                value={sfolder}
                onChange={e => setSfolder(e.target.value)}
                fullWidth
                label={getLocalizedString("subfolder")}
              />
            </Grid>
            <Grid container item alignItems="center" xs={12} sm={Boolean(groupCode) ? 6 : 12} style={gridStyle}>
              <FormControl style={{ width: "100%" }}>
                <InputLabel required id="type-label">{getLocalizedString("fileType")}</InputLabel>
                <Select
                  fullWidth
                  value={type}
                  labelId="type-label"
                  id="type-select"
                  onChange={(e) => setType(e.target.value)}
                  margin={"none"}
                >
                  {
                    typeList.map(item => (
                      <MenuItem
                        className={"whiteText"}
                        key={item.key}
                        value={item.key}
                      >
                        {item.value}
                      </MenuItem>
                    ))
                  }
                </Select>
              </FormControl>
            </Grid>
            {
              Boolean(groupCode) &&
              <Grid container item alignItems="center" xs={12} sm={6} style={gridStyle}>
                <FormControl style={{ width: "100%" }}>
                  <InputLabel required id="permission-label">{getLocalizedString("permissions")}</InputLabel>
                  <Select
                    fullWidth
                    value={permission}
                    labelId="permission-label"
                    id="permission-select"
                    onChange={(e) => setPermission(e.target.value)}
                    margin={"none"}
                  >
                    {
                      permissionList.map(item => (
                        <MenuItem
                          className={"whiteText"}
                          key={item.key}
                          value={item.key}
                        >
                          {item.value}
                        </MenuItem>
                      ))
                    }
                  </Select>
                </FormControl>
              </Grid>
            }
            <Grid container item alignItems="center" xs={12} style={gridStyle}>
              <TextField
                required
                value={description}
                onChange={e => setDescription(e.target.value)}
                fullWidth
                label={getLocalizedString("description")}
              />
            </Grid>
            <Grid container item alignItems="center" xs={12} sm={6} style={gridStyle}>
              <MuiPickersUtilsProvider utils={DateFnsUtils} locale={dateLocale}>
                <DatePicker
                  style={{ marginTop: 0, marginBottom: 0, width: "100%" }}
                  label={getLocalizedString("year")}
                  clearable
                  clearLabel={getLocalizedString("clear")}
                  cancelLabel={getLocalizedString("cancel")}
                  okLabel={getLocalizedString("ok")}
                  invalidDateMessage=""
                  format="yyyy"
                  margin="normal"
                  value={year}
                  onChange={(e) => setYear(e)}
                  views={["year"]}
                />
              </MuiPickersUtilsProvider>
            </Grid>
            <Grid container item alignItems="center" xs={12} sm={6} style={gridStyle}>
              <MuiPickersUtilsProvider utils={DateFnsUtils} locale={dateLocale}>
                <DatePicker
                  style={{ marginTop: 0, marginBottom: 0, width: "100%" }}
                  label={getLocalizedString("month")}
                  clearable
                  clearLabel={getLocalizedString("clear")}
                  cancelLabel={getLocalizedString("cancel")}
                  okLabel={getLocalizedString("ok")}
                  invalidDateMessage=""
                  format="MM"
                  margin="normal"
                  value={month}
                  onChange={(e) => setMonth(e)}
                  views={["month"]}
                />
              </MuiPickersUtilsProvider>
            </Grid>
          </Grid>
          <Grid container alignItems="center" item xs={12} style={{ ...gridStyle, height: "", paddingTop: 10, paddingBottom: 10 }}>
            <MuiPickersUtilsProvider utils={DateFnsUtils} locale={dateLocale}>
              <DatePicker
                fullWidth
                label={getLocalizedString("digitalFolderFechadevencimiento")}
                clearable
                clearLabel={getLocalizedString("clear")}
                cancelLabel={getLocalizedString("cancel")}
                okLabel={getLocalizedString("ok")}
                disablePast
                minDate={isEdit ? data.expirationDate : getTomorrow()}
                format="dd/MM/yyyy"
                margin={"none"}
                invalidDateMessage=''
                minDateMessage=''
                maxDateMessage=''
                value={expirationDate}
                onChange={(e) => setExpirationDate(e)}
              />
            </MuiPickersUtilsProvider>
            <Typography className={"whiteText"} variant="caption" gutterBottom>
              {getLocalizedString("annexDateHelper")}
            </Typography>
          </Grid>
          <FileInput
            acceptedFormats={['.jpg', '.bmp', '.png', '.jpeg', '.rar', '.zip', '.gz', '.tar', '.xls', '.xlsx', '.txt', '.rtf', '.doc', '.docx', '.pdf', '.html', '.ppt', '.pptx']}
            handleChange={(files) => setFiles(files)}
            preSubmitCallback={() => setFailedDocuments(null)}
            multiple={!isEdit}
          />
          <FileList
            files={files}
            filesWithError={failedDocuments}
            handleClick={handleDeleteFileFromList}
            multiple={!isEdit}
          />
        </DialogContent>
        <DialogActions>
          <ButtonDialogAction onClick={handleClose} text={getLocalizedString("cancel")} />
          <ButtonDialogAction onClick={handleSubmit} isAccept text={getLocalizedString("accept")} />
        </DialogActions>
      </div>
    </Dialog>
  );
}

export default UploadFilesDialog;
